import { inject, Injectable } from '@angular/core';
import { type Dictionary } from '@ngrx/entity';
import groupBy from 'lodash-es/groupBy';

import {
  type CanvasElement,
  type CanvasElementFrom,
  type ColumnId,
  getFormGroupLikeWrapper,
  type WrapperRefs,
} from '@blaze-runtime/engine-domain';
import { getWorkflowNodeBase } from '@blaze-runtime/workflow-domain';
import {
  type DataBindingSourceInfo,
  getActiveBinding,
  updateMergeNodeAssignmentsFromColumns,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { findColumn } from '@blaze-shared/schema-domain';
import { isDefined } from '@blaze-shared/shared-util';
import { normalizeVarName } from '@blaze-shared/variables-util';
import {
  type AssignWorkflowNode,
  getAutoDisplayInfo,
  type IfWorkflowNode,
  isMergeNode,
  type MergeWorkflowNode,
  type WorkflowFlatModel,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import {
  isEditableForm,
  isElementType,
  isFormList,
} from '../../canvas-elements/element-model/element-type-guards';
import { getWizardSteps } from '../../elements/wizard/wizard.utils';
import { isElementChildOfAtSlot } from '../../utils/layout/relationship';
import { FormGroupUtilsService } from '../form-group-utils.service';

import { formGroupDataSourceInput, formGroupDataSourceVariable } from './form-group-naming';
import { FormWriteNodesService } from './form-write-nodes.service';

/** The input name under which a wizard reports which of its steps have been filled in. */
const FORMS_STATUS = 'formsFilled';

/** What each linking column is written with: the variable holding the id on the other side. */
type LinkSubstitutions = Record<string, Partial<Record<string, { value: string; label: string }>>>;

interface FormGroupDatasetLink {
  columnKey: string | undefined;
  datasetId: string;
  formDataSource: DataBindingSourceInfo;
  formId: string;
  formIds: string[];
  /** The dataset the group itself is bound to; the others link to it. */
  isMain: boolean;
  /** Whether the form collects many rows, which makes it a merge rather than an upsert. */
  multiple: boolean;
  /** Whether the step collecting it may be skipped, which makes its part of the workflow optional. */
  optional: boolean;
  /** Whether the group's own dataset holds the key, so the id flows out of this dataset. */
  outgoing: boolean;
}

/**
 * A form-group binding update that arrives before the group itself has one.
 *
 * Child forms and their group are bound one after another, so the child's update can run first.
 * Generating then would produce a workflow against a half-configured group, so the attempt is
 * abandoned rather than failed — the group's own update regenerates it a moment later.
 */
export class FormGroupWorkflowGenerationIgnorableError extends Error {}

/**
 * Works out which nodes save a form group: one write per dataset it spans, ordered so that every
 * id exists before the row referencing it.
 *
 * Forms whose key lives on the group's own dataset must be written first, so their new ids can be
 * assigned into the group's row; forms holding a key back to the group must be written after it,
 * for the same reason in reverse. That ordering is the whole point of generating this workflow
 * rather than one per form.
 */
@Injectable({ providedIn: 'root' })
export class FormGroupActionNodesService {
  private readonly formGroupUtilsService = inject(FormGroupUtilsService);
  private readonly formWriteNodesService = inject(FormWriteNodesService);
  private readonly schemaService = inject(SchemaService);

  /** The nodes that write every form, ordered so each id exists before the row referencing it. */
  buildActionModel({
    entities,
    formGroup,
    oldActions,
    refs,
  }: {
    entities: Dictionary<CanvasElement>;
    formGroup: CanvasElementFrom<'formGroup' | 'wizard'>;
    oldActions: WorkflowNode[];
    refs: WrapperRefs;
  }): WorkflowFlatModel[] {
    const optionalSteps = isElementType(formGroup, 'wizard')
      ? getWizardSteps(formGroup.props)
          .map((step, i) =>
            step.type === 'data' && !step.required ? formGroup.slots.ids[i] : undefined,
          )
          .filter(isDefined)
      : undefined;
    const formGroupDataSource = getActiveBinding(formGroup.props.data, 'value')?.dataSource;
    if (!formGroupDataSource?.name) {
      throw new FormGroupWorkflowGenerationIgnorableError(
        'form group data source missing or unnamed',
      );
    }

    const links = this.collectDatasetLinks({
      entities,
      formGroup,
      formGroupDataSource,
      optionalSteps,
      refs,
    });
    const mainLinks = links.filter(({ isMain }) => isMain);
    const outgoingLinks = links.filter(({ isMain, outgoing }) => !isMain && outgoing);
    const incomingLinks = links.filter(({ isMain, outgoing }) => !isMain && !outgoing);
    const linkSubstitutions = this.buildLinkSubstitutions(
      formGroupDataSource,
      outgoingLinks,
      incomingLinks,
    );

    // Outgoing first, then the group's own dataset, then incoming: each id exists before the
    // row that references it.
    return [...outgoingLinks, ...mainLinks, ...incomingLinks].map((link) =>
      this.buildLinkNodes({ entities, formGroup, link, linkSubstitutions, oldActions, refs }),
    );
  }

  /**
   * The nodes that write one dataset: what to write, followed by the assignment publishing the id
   * it produced. A step that may be skipped is wrapped in an `If` on whether it was filled in.
   */
  private buildLinkNodes({
    entities,
    formGroup,
    link,
    linkSubstitutions,
    oldActions,
    refs,
  }: {
    entities: Dictionary<CanvasElement>;
    formGroup: CanvasElementFrom<'formGroup' | 'wizard'>;
    link: FormGroupDatasetLink;
    linkSubstitutions: LinkSubstitutions;
    oldActions: WorkflowNode[];
    refs: WrapperRefs;
  }): WorkflowFlatModel {
    const { columnKey, formDataSource, formId, multiple, optional } = link;
    const idColumn = this.schemaService.getDataSourceIdColumn(
      formDataSource.id,
      formDataSource.sourceType,
    );
    const formName = formGroupDataSourceInput(formDataSource.name, formId, refs);
    const operationNode = this.buildOperationNode({
      entities,
      formGroup,
      formName,
      idColumn,
      isMainDataset: columnKey === undefined,
      link,
      linkSubstitutions,
      oldActions,
      refs,
    });

    const nodes = operationNode
      ? [
          operationNode,
          ...this.formWriteNodesService.getFormGroupCommonActions(formDataSource, multiple),
        ]
      : [
          <AssignWorkflowNode>{
            id: this.formGroupUtilsService.workflowNodeId('assign', formDataSource),
            nodeType: 'Assign',
            outputName: normalizeVarName(formGroupDataSourceVariable(formDataSource)),
            isFormulaValue: false,
            scope: 'Workflow',
            value: `$$${formName}["${idColumn}"]`,
            displayInfos: [getAutoDisplayInfo(formName, idColumn, true)],
          },
        ];

    if (!optional) {
      return { type: 'sequence', nodes };
    }
    return {
      type: 'if',
      node: <IfWorkflowNode>{
        id: this.formGroupUtilsService.workflowNodeId('optionalize', formDataSource),
        nodeType: 'If',
        condition: `$$${FORMS_STATUS}["${formId}"]`,
        displayInfos: [getAutoDisplayInfo(FORMS_STATUS, formId, true)],
      },
      onTrue: nodes,
      // A step nobody filled in still has to publish an id, so the rows referencing it see none.
      onFalse: [
        <AssignWorkflowNode>{
          id: this.formGroupUtilsService.workflowNodeId('assign_optional', formDataSource),
          nodeType: 'Assign',
          outputName: normalizeVarName(formGroupDataSourceVariable(formDataSource)),
          isFormulaValue: false,
          scope: 'Workflow',
          value: '',
        },
      ],
    };
  }

  /**
   * The node that writes the dataset itself: a merge when the form collects many rows, an
   * insert or update when it collects one.
   *
   * @returns nothing when the form writes nothing of its own — a `link` action on the group's own
   * dataset with nothing to link — in which case only the id assignment follows.
   */
  private buildOperationNode({
    entities,
    formGroup,
    formName,
    idColumn,
    isMainDataset,
    link,
    linkSubstitutions,
    oldActions,
    refs,
  }: {
    entities: Dictionary<CanvasElement>;
    formGroup: CanvasElementFrom<'formGroup' | 'wizard'>;
    formName: string;
    idColumn: string;
    isMainDataset: boolean;
    link: FormGroupDatasetLink;
    linkSubstitutions: LinkSubstitutions;
    oldActions: WorkflowNode[];
    refs: WrapperRefs;
  }): WorkflowNode | undefined {
    const { datasetId, formDataSource, formIds, multiple } = link;
    const existingActionNode = oldActions.find(
      ({ id }) =>
        id ===
        this.formGroupUtilsService.workflowNodeId(multiple ? 'merge' : 'upsert', formDataSource),
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const substitutions = linkSubstitutions[formDataSource.name!];

    if (multiple) {
      return <MergeWorkflowNode>{
        ...getWorkflowNodeBase(
          'Merge',
          this.formGroupUtilsService.workflowNodeId('merge', formDataSource),
        ),
        ...existingActionNode,
        version: 2,
        datasetId,
        input: formName,
        assignments: updateMergeNodeAssignmentsFromColumns(
          isMergeNode(existingActionNode) ? existingActionNode : undefined,
          formDataSource.columns,
          formGroup.props.content.action === 'add' ? substitutions : {},
        ),
      };
    }
    return this.formWriteNodesService.getFormGroupActionNode(
      formGroup.props.content.action,
      formDataSource,
      isMainDataset,
      existingActionNode,
      formName,
      this.columnToFormNameMapping(entities, formIds, formDataSource.name, refs),
      substitutions,
      /^@@\w+Id$/,
      idColumn,
      undefined,
    );
  }

  /** Which form each column is collected on, so an assignment reads from the right input. */
  private columnToFormNameMapping(
    entities: Dictionary<CanvasElement>,
    formIds: string[],
    dataSourceName: string | undefined,
    refs: WrapperRefs,
  ): Record<string, string> {
    return Object.fromEntries(
      formIds.flatMap((id) => {
        const form = entities[id];
        if (!isEditableForm(form) || !form.props.content.model) {
          return [];
        }
        const formName = formGroupDataSourceInput(dataSourceName, id, refs);
        return form.props.content.model
          .filter(({ visible }) => visible)
          .map(({ fieldId }) => [fieldId, formName] as [string, string]);
      }),
    );
  }

  /** Groups the group's forms by the dataset they write to, and works out how each links to it. */
  private collectDatasetLinks({
    entities,
    formGroup,
    formGroupDataSource,
    optionalSteps,
    refs,
  }: {
    entities: Dictionary<CanvasElement>;
    formGroup: CanvasElementFrom<'formGroup' | 'wizard'>;
    formGroupDataSource: DataBindingSourceInfo;
    optionalSteps: ColumnId[] | undefined;
    refs: WrapperRefs;
  }): FormGroupDatasetLink[] {
    const datasets = groupBy(getFormGroupLikeWrapper(refs), ({ datasetId }) => datasetId);
    return Object.entries(datasets).map(([datasetId, wrapperEntries]) => {
      const [{ formId, keyColumnId }] = wrapperEntries;
      const formIds = wrapperEntries.map((entry) => entry.formId);
      const form = entities[formId];
      const formDataSource = hasDataProps(form)
        ? getActiveBinding(form.props.data, 'value')?.dataSource
        : undefined;
      if (!formDataSource?.name) {
        throw new Error('form data source missing or unnamed');
      }
      const multiple = isFormList(form);
      if (!keyColumnId) {
        return {
          columnKey: undefined,
          datasetId,
          formDataSource,
          formId,
          formIds,
          isMain: true,
          multiple,
          optional: false,
          outgoing: false,
        };
      }
      return {
        columnKey: keyColumnId,
        datasetId,
        formDataSource,
        formId,
        formIds,
        isMain: false,
        multiple,
        optional: formIds.every(
          (id) =>
            !!optionalSteps?.some((slotId) =>
              isElementChildOfAtSlot(entities, id, formGroup.id, slotId),
            ),
        ),
        outgoing: !!findColumn(formGroupDataSource?.columns, keyColumnId),
      };
    });
  }

  /**
   * What each linking column is written with: the variable holding the id of the row on the other
   * side of the link, which the node writing that row assigned.
   */
  private buildLinkSubstitutions(
    formGroupDataSource: DataBindingSourceInfo,
    outgoingLinks: FormGroupDatasetLink[],
    incomingLinks: FormGroupDatasetLink[],
  ): LinkSubstitutions {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      [formGroupDataSource.name!]: Object.fromEntries(
        outgoingLinks.map(({ columnKey, formDataSource }) => [
          columnKey,
          {
            value: normalizeVarName(formGroupDataSourceVariable(formDataSource)),
            label: formGroupDataSourceVariable(formDataSource),
          },
        ]),
      ),
      ...Object.fromEntries(
        incomingLinks.map(
          ({ columnKey, formDataSource }) =>
            [
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              formDataSource.name!,
              {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                [columnKey!]: {
                  value: normalizeVarName(formGroupDataSourceVariable(formGroupDataSource)),
                  label: formGroupDataSourceVariable(formGroupDataSource),
                },
              },
            ] as const,
        ),
      ),
    };
  }
}
